import React, { useState } from "react";
import { LeadCard, Sec, Fold, InlineAdd } from "./parts";
import { clientSince, deliveryView, noDash } from "./leadModel.mjs";
import { feeLines, feeLabel } from "../moneyModel.mjs";
import ClientBrief from "./ClientBrief";

/*
 * CLIENT: the account (the brief's Client row; matrix 1e: "Signed fee · since · AM ·
 * ad account (unlinked first)"). Undrawn as a frame, so built in the drawn card
 * grammar on Gabriel's ruling (2026-10-01) from Pluto's fee hero and the rail's
 * unlinked card (frame 1b). No sales furniture anywhere on it.
 *
 *   - THE SIGNED FEE is the hero, one line per part (signed_fee_structure), labelled
 *     by what is on record ("Signed fee", or "Fee · from the final agreement · not
 *     signed yet"); empty, "not recorded" with its Add, because Deal and money
 *     points here for the fee.
 *   - CLIENT SINCE, in the meta.
 *   - OUR ACCOUNT MANAGER: the account's CM as our auditor holds it, when the ad
 *     account is linked; otherwise a Proposed row, since the CRM has no field.
 *   - THEIR LINKEDIN AD ACCOUNT and WHAT OUR OWN ADS REACHED THEM WITH (AdDelivery's
 *     logic, restyled): drawn UNLINKED FIRST, because the auditor is not connected in
 *     production (AUDITOR_DATABASE_URL unset), so that is what ships: "No ad account
 *     linked", why, and Link account as a Proposed tag. Connected but unmatched says
 *     that instead. Reach never carries a spend figure: LinkedIn attributes cost only
 *     where something was clicked, so it reads as "we spent nothing reaching them".
 *   - THE CLIENT BRIEF (ClientBrief.js): Open in Slack, or write one for an older client.
 *
 * Props:
 *   record   the shell's bundle: company (won_at, signed_fee_structure), act (ads,
 *            ads_line, delivery, delivery_line, audit_line, auditor_connected)
 *   ops      ops.patchCompany(body) (the fee's Add)
 */
export default function Client({ record, ops }) {
  const co = record.company || {};
  const act = record.act || {};
  const fee = feeLines(co.signed_fee_structure);
  const reach = deliveryView(act.delivery);
  const [months, setMonths] = useState(false);
  const cm = act.ads && act.ads.cm_name ? act.ads.cm_name : null;

  return (
    <LeadCard label="The account" meta={clientSince(co, { now: record.now })}>
      <div>
        <div className="rec-lead__small">{feeLabel(co, act)}</div>
        {fee.length ? (
          <>
            <div className="rec-hero">{fee[0]}</div>
            {fee.length > 1 && (
              <div className="rec-lead__lines">
                {fee.slice(1).map((l) => (
                  <span key={l}>{l}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className="rec-sub">
            not recorded ·{" "}
            <InlineAdd multiline placeholder={"$3,500/mo base\n15% of spend $10K–$50K"} hint="One line per part, as the agreement words it." onSave={(text) => ops.patchCompany({ signed_fee_structure: text })} />
          </span>
        )}
      </div>

      <div className="rec-kv rec-sep">
        <span>Account manager</span>
        {cm ? (
          <span>
            {cm} <span className="rec-meta">· from our auditor</span>
          </span>
        ) : (
          <span className="rec-proposed-wrap">
            <span className="rec-inert">Add account manager</span>
            <span className="rec-proposed">Proposed</span>
          </span>
        )}
        {act.audit_line && (
          <>
            <span>Audit</span>
            <span>{noDash(act.audit_line)}</span>
          </>
        )}
      </div>

      {act.ads ? (
        <Sec>
          <span className="rec-lead__small">Their LinkedIn ad account</span>
          <span>{noDash(act.ads_line) || "Linked in our auditor"}</span>
        </Sec>
      ) : (
        <div className="rec-dashedbox">
          <span className="rec-strong">No ad account linked</span>
          <span className="rec-sub">
            {act.auditor_connected
              ? "No ad account in our auditor matches this company."
              : "Their account and what our own ads reached them with come from our auditor, which isn’t connected yet."}
          </span>
          <span className="rec-proposed-wrap">
            <span className="rec-inert">Link account</span>
            <span className="rec-proposed">Proposed</span>
          </span>
        </div>
      )}

      {reach && (
        <Sec>
          <span className="rec-lead__small">What our ads reached them with{reach.span ? ` · ${reach.span}` : ""}</span>
          <div className="rec-lead__metrics">
            {reach.metrics.map((m) => (
              <div key={m.label} className="rec-lead__metric">
                <span className="rec-lead__metricn">{m.value}</span>
                <span className="rec-meta">{m.label}</span>
              </div>
            ))}
          </div>
          {reach.months.length > 1 && (
            <div className="rec-lead__folds rec-lead__folds--flat">
              <Fold title="By month" open={months} onToggle={() => setMonths((v) => !v)}>
                <div className="rec-kv rec-kv--96">
                  {reach.months.map((m) => (
                    <React.Fragment key={m.key}>
                      <span>{m.label}</span>
                      <span>{m.impressions} impressions · {m.clicks} clicks</span>
                    </React.Fragment>
                  ))}
                </div>
              </Fold>
            </div>
          )}
        </Sec>
      )}

      <ClientBrief companyId={co.id} />
    </LeadCard>
  );
}
